import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from 'types';
import { ProductOrderItem } from 'types/api/orders.type';
import { PriceType } from 'types/api/pricing.type';

import { priceTypeStore } from 'lib/priceTypeStore';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { useOrder } from 'pages/Orders/components/Order/Order.hooks';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';
import { InfoModal } from 'components/InfoModal';
import { Select } from 'components/Select';

import { useProductTable } from './ProductTable.hooks';
import * as S from './ProductTable.styles';
import { LazyMount } from './components/LazyMount';
import { OrderFooter } from './components/OrderFooter';
import { StyleRow } from './components/StyleRow';
import { StyleRowSkeleton } from './components/StyleRowSkeleton';
import { TemplateModal } from './components/TemplateModal';

type DeleteTarget =
  | { type: 'colour'; item: ProductOrderItem }
  | { type: 'style'; styleKey: string }
  | null;

/** Style rows rendered immediately; the rest render as they near the viewport. */
const EAGER_STYLE_ROWS = 8;

/** Skeleton rows shown while the order loads — enough to fill a large screen. */
export const LOADING_SKELETON_ROWS = 6;

const PRICE_TYPE_LABEL_KEYS: Record<PriceType, string> = {
  DISTRIBUTOR: 'catalogue.distributorPrice',
  WHOLESALE: 'catalogue.wholesalePrice',
  RETAIL: 'catalogue.retailPrice',
};

export function ProductTable(): React.ReactElement {
  const {
    styleGroups,
    orderTotal,
    isSend,
    onQuantityBlur,
    onDeliveryCodeChange,
    onDeleteColour,
    onDeleteStyle,
    isDownloading,
    isUploading,
    isTemplateModalVisible,
    onOpenTemplateModal,
    onCloseTemplateModal,
    onDownloadTemplateWithParams,
    onUploadProducts,
    fileInputRef,
    onFileSelected,
    control,
    selectedPriceType,
    selectedPriceTypeSet,
    availablePriceTypes,
  } = useProductTable();
  const { order, orderHeader, isBasicInfoLoading } = useOrder();
  const { t } = useTranslation();
  const viewPrice = usePriceVisible();

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stable handlers keep the memoized rows from re-rendering on every table render.
  const handleDeleteColour = useCallback(
    (item: ProductOrderItem) => setDeleteTarget({ type: 'colour', item }),
    []
  );
  const handleDeleteStyle = useCallback(
    (styleKey: string) => setDeleteTarget({ type: 'style', styleKey }),
    []
  );

  const priceTypeOptions: Option[] = useMemo(
    () =>
      availablePriceTypes.map((pt) => ({
        value: pt,
        label: t(PRICE_TYPE_LABEL_KEYS[pt] as any), // eslint-disable-line
      })),
    [availablePriceTypes, t]
  );

  const selectedOption = useMemo(
    () => priceTypeOptions.find((o) => o.value === selectedPriceType),
    [priceTypeOptions, selectedPriceType]
  );

  const handlePriceTypeChange = (option: Option) => {
    selectedPriceTypeSet(option.value as PriceType);
    priceTypeStore.setPriceType(option.value as PriceType);
  };

  const showPriceSelector = viewPrice && availablePriceTypes.length > 1;

  // Until the order arrives (and for the one render between the header landing
  // and its items being synced into the order) the table is loading, not empty.
  const isOrderLoading = isBasicInfoLoading || (!!orderHeader && !order);

  if (styleGroups.length === 0 && isOrderLoading) {
    return (
      <S.Container
        aria-busy="true"
        aria-label={t('order.loading')}
        data-testid="order-loading"
      >
        {Array.from({ length: LOADING_SKELETON_ROWS }, (_, i) => (
          <StyleRowSkeleton key={i} />
        ))}
      </S.Container>
    );
  }

  if (styleGroups.length === 0) {
    return (
      <S.EmptyState>
        <Headline scale="xl3">{t('order.isEmpty')}</Headline>
        {orderHeader && (
          <S.EmptyStateActions>
            <Button
              motive="actionTertiary"
              onClick={onOpenTemplateModal}
              disabled={isDownloading}
              isLoading={isDownloading}
              aria-label={t('orderTable.downloadTemplate')}
              fontFamily="LFT Zeno"
              scale="xs"
            >
              {t('orderTable.downloadTemplate')}
            </Button>
            <Button
              motive="actionTertiary"
              onClick={onUploadProducts}
              disabled={isUploading}
              isLoading={isUploading}
              aria-label={t('orderTable.uploadProducts')}
              fontFamily="LFT Zeno"
              scale="xs"
            >
              {t('orderTable.uploadProducts')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={onFileSelected}
            />
          </S.EmptyStateActions>
        )}
        {isTemplateModalVisible && (
          <TemplateModal
            onClose={onCloseTemplateModal}
            onDownload={onDownloadTemplateWithParams}
            isLoading={isDownloading}
            season={orderHeader?.season ?? ''}
            collectionCode={orderHeader?.collectionCode ?? ''}
          />
        )}
      </S.EmptyState>
    );
  }

  // Order-level header values applied to every Style_Row.
  const headerDeliveryCode = orderHeader?.deliveryCode ?? '';

  return (
    <S.Container>
      {showPriceSelector ? (
        <S.PriceTypeSelectorRow>
          <Select
            name="orderPriceType"
            label={String(t('catalogue.priceType'))}
            options={priceTypeOptions}
            value={selectedOption}
            onChange={handlePriceTypeChange}
            motive="default"
          />
        </S.PriceTypeSelectorRow>
      ) : null}
      {styleGroups.map((group, index) => (
        <LazyMount
          key={group.styleKey}
          eager={index < EAGER_STYLE_ROWS}
          placeholder={<StyleRowSkeleton colours={group.colours.length} animated={false} />}
        >
          <StyleRow
            group={group}
            isSend={isSend}
            control={control}
            headerDeliveryCode={headerDeliveryCode}
            selectedPriceType={selectedPriceType}
            onQuantityBlur={onQuantityBlur}
            onDeliveryCodeChange={onDeliveryCodeChange}
            onDeleteColour={handleDeleteColour}
            onDeleteStyle={handleDeleteStyle}
          />
        </LazyMount>
      ))}
      <OrderFooter
        quantity={orderTotal.quantity}
        price={orderTotal.price}
        currency={orderTotal.currency}
      />
      {deleteTarget && (
        <InfoModal
          title={t('common.delete')}
          info={t('common.deleteProductConfirm')}
          onClose={() => {
            if (!isDeleting) setDeleteTarget(null);
          }}
          onAction={async () => {
            setIsDeleting(true);
            try {
              if (deleteTarget.type === 'colour') {
                await onDeleteColour(deleteTarget.item);
              } else {
                await onDeleteStyle(deleteTarget.styleKey);
              }
            } finally {
              setIsDeleting(false);
            }
            setDeleteTarget(null);
          }}
          actionText={t('common.delete')}
          isLoading={isDeleting}
        />
      )}
    </S.Container>
  );
}
